import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    GetOverviewProjectMeasuringPointsDocument,
    type GetOverviewProjectMeasuringPointsQueryVariables,
} from '@/graphql/types';
import warning from '../../../../utils/logger';
import type { ProjectOverviewRow } from '../types';
import { unmaskOverviewMeasuringPoints } from '../utils/measuringPoints';

/**
 * The query the expanded rows of a project read. Also refetched on its own after an action
 * on one of its measuring points.
 */
export function getExpandedMeasuringPointsQuery(projectId: string, archived: boolean) {
    const variables: GetOverviewProjectMeasuringPointsQueryVariables = {
        archived,
        projectIds: [projectId],
    };
    return { query: GetOverviewProjectMeasuringPointsDocument, variables };
}

function keepOnly(ids: ReadonlySet<string>, allowedIds: string[]) {
    const kept = new Set(Array.from(ids).filter((id) => allowedIds.includes(id)));
    return kept.size === ids.size ? ids : kept;
}

/**
 * Fetches the measuring points of the project rows that were just expanded, or came into
 * view expanded, in one request, and writes each project's share into the query its row
 * reads. The rows only read the cache, so expanding all projects, or returning to a page
 * with many expanded projects, costs one request instead of one per row.
 *
 * The projects whose request failed are returned as `failedProjectIds`, for their rows to say
 * so. Collapsing and expanding one of them fetches it again.
 */
export function useLoadExpandedMeasuringPoints({
    projectRows,
    isProjectExpanded,
    archived,
    paused,
}: {
    projectRows: ProjectOverviewRow[];
    isProjectExpanded: (project: ProjectOverviewRow) => boolean;
    archived: boolean;
    // While the page for new filters loads there are no rows, which is not the projects leaving
    // view: when they are still in the result, they are not fetched again.
    paused: boolean;
}) {
    const client = useApolloClient();
    const [failedProjectIds, setFailedProjectIds] = useState<ReadonlySet<string>>(() => new Set());
    // The projects that were expanded and in view last time, so only new ones are fetched.
    // Collapsed projects drop out, so expanding one again refreshes it.
    const loadedRef = useRef({ archived, projectIds: new Set<string>() });

    const expandedProjectIdsKey = useMemo(
        () =>
            projectRows
                .flatMap((projectRow) =>
                    projectRow.measuringPointCount > 0 && isProjectExpanded(projectRow)
                        ? [projectRow.projectId]
                        : []
                )
                .join(','),
        [projectRows, isProjectExpanded]
    );

    useEffect(() => {
        if (paused) {
            return;
        }

        const expandedProjectIds = expandedProjectIdsKey ? expandedProjectIdsKey.split(',') : [];
        const loaded = loadedRef.current;
        const projectIds = expandedProjectIds.filter(
            (projectId) => loaded.archived !== archived || !loaded.projectIds.has(projectId)
        );
        loadedRef.current = { archived, projectIds: new Set(expandedProjectIds) };

        const notRefetchedProjectIds = expandedProjectIds.filter(
            (projectId) => !projectIds.includes(projectId)
        );
        setFailedProjectIds((failed) => keepOnly(failed, notRefetchedProjectIds));

        if (projectIds.length === 0) {
            return;
        }

        void client
            .query({
                query: GetOverviewProjectMeasuringPointsDocument,
                variables: { archived, projectIds },
                fetchPolicy: 'network-only',
            })
            .then(
                ({ data }) => {
                    const measuringPoints = unmaskOverviewMeasuringPoints(data.measuringPoints);
                    projectIds.forEach((projectId) =>
                        client.writeQuery({
                            ...getExpandedMeasuringPointsQuery(projectId, archived),
                            data: {
                                measuringPoints: measuringPoints.filter(
                                    (measuringPoint) => measuringPoint.project === projectId
                                ),
                            },
                        })
                    );
                },
                (requestError: unknown) => {
                    warning({ message: requestError });
                    setFailedProjectIds((failed) => new Set([...failed, ...projectIds]));
                }
            );
    }, [archived, client, expandedProjectIdsKey, paused]);

    return { failedProjectIds };
}
